import React from 'react'
import {PARENT_ROW, efficiencyOfRow, workHoursOfRow} from '../../helpers/reportRows'
import {Tag, Tooltip} from 'antd'
import {
    efficiencyToneOf,
    gradeLabelOf,
    gradeTagTextOf,
    pureEfficiencyNoteOf,
    belowRatioOf,
    hoursNoteOf,
    relativeToneOf,
    spreadGeometryOf,
} from '../../helpers/efficiencyReport'
import Sparkline from './Sparkline'
import {DASH, formatDecimal, formatEfficiency, formatHours, formatInt, formatPercent} from '../../helpers/format'
import {OUTPUT_TOOLTIPS} from '../../constants/glossary'

/**
 * Nhóm cột HIỆU SUẤT của hai báo cáo M18 (FAC-26417 FR-003).
 *
 * Để ở file riêng chứ không nhét vào `OutputReportPage.js`: file đó đã 1.236 dòng và phục vụ CẢ HAI
 * màn, thêm năm cột vào trong là vượt 1.400 và mọi lần sửa một cột phải cuộn qua mười một cột khác.
 *
 * ⚠️ Màu lấy đúng bốn token đang có (`GRADE_LABELS` ở `constants`), KHÔNG thêm màu mới — yêu cầu
 *    "giữ nguyên format màu". A `$vc-success` · B `$vc-primary` · C `$vc-warning` · D `$vc-danger`.
 *
 * ⚠️ Màu KHÔNG phải kênh thông tin duy nhất (AC-UI-01): mọi ô đều có CON SỐ, và ô không áp dụng
 *    được ghi bằng CHỮ ("Không áp dụng") chứ không bỏ trống. Màn hình tại xưởng chất lượng thấp.
 */

/** Ô hiệu suất: con số + tông màu theo hạng. Trả gạch ngang khi chưa đo được. */
const EfficiencyValue = ({value, tone, suffix = ''}) => {
    if (value === null || value === undefined) return <span className="VcZero">{DASH}</span>

    return (
        <strong className={tone ? `VcEffValue VcEffValue--${tone}` : 'VcEffValue'}>
            {formatEfficiency(value)}
            {suffix}
        </strong>
    )
}

/**
 * Ô Hiệu suất kèm dòng phụ "thuần ..." khi hai con số khác nhau sau khi làm tròn.
 *
 * ⚠️ Dòng phụ dùng chữ nhỏ và màu phụ, KHÔNG tô màu theo hạng. Hai con số cùng tông màu cạnh nhau
 *    đọc thành hai chỉ số ngang hàng, trong khi cái quyết định xếp hạng chỉ là con số TRÊN.
 */
const EfficiencyCell = ({eff, thresholds}) => {
    const note = pureEfficiencyNoteOf(eff)

    return (
        <>
            <EfficiencyValue value={eff.efficiency} tone={efficiencyToneOf(eff.efficiency, thresholds)}/>
            {note ? (
                /*
                 * ⚠️ `Tooltip` phải bọc ĐÚNG phần tử mang `VcHasTooltip`, và phần tử đó phải là
                 *    `<span>`. Hai ràng buộc, từng vi phạm mỗi cái một lần:
                 *      · `VcHasTooltip` là `border-bottom: 1px dotted` — đặt lên `<div>` (block)
                 *        trong ô rộng 110px căn phải thì gạch chấm kéo suốt cả ô, đọc thành vạch
                 *        kẻ bảng chứ không phải gợi ý rê chuột.
                 *      · antd 4 clone CON TRỰC TIẾP và gắn `onMouseEnter` lên nó — bọc ra ngoài
                 *        `<div>` thì vùng rê chuột là trọn bề ngang ô trong khi chữ chỉ ~60px nằm
                 *        sát phải, và mũi tên tooltip trỏ vào giữa ô thay vì vào chữ.
                 *    22 chỗ khác trong repo đều bọc đúng phần tử mang class. Đây từng là chỗ duy
                 *    nhất tách hai.
                 *
                 * ⚠️ Câu giải thích lấy từ `OUTPUT_TOOLTIPS`, không viết tay ở đây: `glossary.js`
                 *    tự đặt luật "một nguồn", và bản viết tay trước đó thiếu đúng hai vế quan
                 *    trọng nhất (mẫu số giữ nguyên nên luôn ≤ Hiệu suất; công đoạn đếm theo ĐƠN
                 *    luôn bằng nhau). Phần CHÊNH thì phải ghép ở đây vì nó theo từng dòng.
                 */
                <div className="VcEffPure">
                    <Tooltip title={`${OUTPUT_TOOLTIPS.efficiencyPure} — dòng này thấp hơn ${formatDecimal(note.gapPoints, 1)} điểm`}>
                        <span className="VcHasTooltip">thuần {formatEfficiency(note.value)}</span>
                    </Tooltip>
                </div>
            ) : null}
        </>
    )
}

/**
 * Ô "không áp dụng" — tài khoản hệ thống, hoặc công đoạn không quy được về người.
 *
 * ⚠️ Ghi bằng CHỮ chứ không bỏ trống. Ô trống đọc hệt như "chưa có dữ liệu", và người dùng sẽ đi
 *    tìm xem ai quên nhập giờ cho `3D Partner` — một việc không bao giờ có kết quả.
 */
const NotApplicable = () => (
    <Tooltip title="Tài khoản hệ thống hoặc công đoạn không quy được về người — không chấm hiệu suất">
        <span className="VcMuted VcHasTooltip">Không áp dụng</span>
    </Tooltip>
)

/**
 * Năm cột của màn THEO NHÂN VIÊN: Giờ làm thực tế · Hiệu suất · Chỉ số tương đối · Xếp hạng · Xu hướng.
 *
 * `header` là hàm dựng tiêu đề có tooltip của trang gọi tới — truyền vào thay vì dựng lại, để tooltip
 * của nhóm cột mới đọc từ CÙNG một nguồn glossary với mười hai cột cũ.
 */
export const actorEfficiencyColumns = ({header, thresholds}) => [
    {
        title: header('actualHours', 'Giờ làm thực tế'),
        className: 'VcNewCol',
        key: 'actualHours',
        align: 'right',
        width: 70,
        render: (text, row) => {
            const eff = efficiencyOfRow(row)

            if (!eff) return <span className="VcZero">{DASH}</span>
            if (eff.notApplicable) return <NotApplicable/>

            // Dòng cha hiện giờ ĐÃ NHẬP, dòng con hiện giờ PHÂN BỔ — xem `workHoursOfRow`.
            const hours = workHoursOfRow(row, eff)

            if (hours === null || hours === undefined) return <span className="VcZero">{DASH}</span>

            /*
             * ⚠️ HAI loại giờ, và trước đây chúng trông GIỐNG HỆT nhau: "176,0 h" ở dòng cha là giờ
             *    ĐÃ NHẬP của cả kỳ, "32,0 h" ở dòng con là giờ PHÂN BỔ cho một công đoạn. Không
             *    chú thích thì người đọc cộng các dòng con lại và thấy không bằng dòng cha —
             *    chênh lệch là phần chưa quy đổi được (In/Cắt/Khắc) — rồi báo lỗi số liệu.
             *
             *    Dòng cha kèm SỐ NGÀY (FR-004) vì giờ không có số ngày đi cùng thì không so được:
             *    40 giờ của 5 ngày và 40 giờ của 2 ngày là hai câu chuyện khác nhau.
             */
            /*
             * ⚠️ Dòng cha đi với `enteredDayCount` (số ngày ĐÃ NHẬP giờ), KHÔNG với `dayCount`.
             *    `dayCount` đếm ngày PHÂN BỔ ĐƯỢC, mà thợ chỉ làm In/Cắt/Khắc thì `by_detect` chưa
             *    quy đổi được giờ ⇒ `dayCount = 0` trong khi chấm công có đủ 176 giờ. Ô in ra
             *    "176,0 h · 0 ngày" — hai nửa của cùng một ô nói ngược nhau.
             */
            const suffix = hoursNoteOf(row.type === PARENT_ROW, eff)

            /*
             * ⚠️ Ghi chú xuống DÒNG RIÊNG, không nối sau con số. Nối vào thì ô thành
             *    "139,0 h 17 ngày" trên một dòng; cột hẹp theo con số giờ nên nó tự ngắt ở chỗ
             *    gần nhất và ra "139,0 h 17" / "ngày" — tách đôi một cụm chỉ có nghĩa khi đi liền.
             *    Hai dòng rõ ràng: giờ ở trên, số ngày ở dưới. Cùng khuôn `.VcRedoCell__percent`
             *    và `.VcEffPure` — ba ô có dòng phụ trong cùng một bảng thì phải trông giống nhau.
             */
            /*
             * ⚠️ `VcHoursStack`, KHÔNG phải `VcHoursCell`. Lớp kia là ô của lưới Giờ làm thực tế
             *    (`ActualHoursGrid`) và nó mang `align-items: center` + nền + padding của ô nhập —
             *    mượn về đây là "139,0 h / 17 ngày" bị căn GIỮA trong một cột `align: 'right'`,
             *    lệch với mọi cột số còn lại; và về sau sửa style ô lưới giờ làm thì báo cáo M18
             *    đổi theo mà không ai ngờ. (Vòng review 2, R2.)
             */
            return (
                <div className="VcHoursStack">
                    <div>{formatHours(hours, 1)} h</div>
                    {suffix && <div className="VcMuted VcHoursNote">{suffix}</div>}
                </div>
            )
        },
    },
    {
        title: header('efficiency', 'Hiệu suất'),
        className: 'VcNewCol',
        key: 'efficiency',
        align: 'right',
        width: 105,
        render: (text, row) => {
            const eff = efficiencyOfRow(row)

            if (!eff) return <span className="VcZero">{DASH}</span>
            if (eff.notApplicable) return <NotApplicable/>

            return <EfficiencyCell eff={eff} thresholds={thresholds}/>
        },
    },
    {
        title: header('relativeIndex', 'Chỉ số tương đối'),
        className: 'VcNewCol',
        key: 'relativeIndex',
        align: 'right',
        width: 75,
        render: (text, row) => {
            const eff = efficiencyOfRow(row)

            if (!eff || eff.notApplicable) return <span className="VcZero">{DASH}</span>
            if (eff.relativeIndex === null || eff.relativeIndex === undefined) {
                return (
                    /*
                     * Lý do khác nhau theo VAI của dòng: ô tài khoản × công đoạn thì là "chưa đủ
                     * giờ tại công đoạn này"; dòng cha là một NGƯỜI, không thuộc công đoạn nào,
                     * nên câu đó vô nghĩa ở đó.
                     */
                    <Tooltip
                        title={
                            row.type === PARENT_ROW
                                ? 'Chưa công đoạn nào của người này đủ giờ phân bổ để so với mặt bằng'
                                : 'Chưa đủ giờ phân bổ tại công đoạn này để so với mặt bằng'
                        }
                    >
                        <span className="VcZero VcHasTooltip">{DASH}</span>
                    </Tooltip>
                )
            }

            const tone = relativeToneOf(eff.relativeIndex)

            // Hiện dạng `1,50` chứ không phải `150%`: đây là TỶ SỐ so mặt bằng, không phải một tỷ lệ
            // phần trăm — in `%` là mời người đọc cộng nó với cột Hiệu suất bên cạnh.
            return (
                <strong className={tone ? `VcEffValue VcEffValue--${tone}` : 'VcEffValue'}>
                    {formatDecimal(eff.relativeIndex, 2)}
                </strong>
            )
        },
    },
    {
        title: header('grade', 'Xếp hạng'),
        className: 'VcNewCol',
        key: 'grade',
        align: 'center',
        width: 75,
        render: (text, row) => {
            // Chỉ dòng TÀI KHOẢN có hạng — hạng là kết luận về một con người, không phải về một ô
            // tài khoản × công đoạn.
            if (row.type !== 'actor') return <span className="VcZero">{DASH}</span>

            const eff = row.group && row.group.efficiency

            if (!eff || eff.notApplicable) return <NotApplicable/>

            const grade = eff.grade && eff.grade.grade
            const label = gradeLabelOf(grade)

            if (!grade) {
                /*
                 * ⚠️ Chưa xếp hạng thì nói VÌ SAO. "Chưa xếp hạng" không kèm lý do là câu khiến
                 *    người dùng kết luận màn hình hỏng — và lý do luôn là một việc họ sửa được
                 *    (nhập thêm giờ, chờ đủ ngày).
                 */
                return (
                    <Tooltip title={(eff.grade && eff.grade.reason) || label.title}>
                        <span className="VcMuted VcHasTooltip">{label.title}</span>
                    </Tooltip>
                )
            }

            /*
             * ⚠️ Hạng LUÔN kèm chữ, không bao giờ chỉ "A" (BR-3.3 / AC-UI-01). Bản trước để nghĩa
             *    của hạng trong tooltip: màu là kênh duy nhất phân biệt bốn mức, mà bảng này được
             *    in ra giấy và được đọc trên màn hình xưởng chất lượng thấp — hai chỗ không có
             *    tooltip và không có màu đáng tin.
             */
            return (
                <span className="VcGradeCell">
                    <Tag color={label.color}>{gradeTagTextOf(eff.grade.grade)}</Tag>
                    {/*
                      * ⚠️ Hạng được chấm trên những ngày ĐÃ có giờ. Còn ngày thiếu giờ thì hạng
                      *    này mới là hạng của một phần kỳ, và người bị chấm có quyền biết điều đó
                      *    ngay trên ô chấm điểm họ — không phải đi tìm trong một banner ở đầu
                      *    trang nói về cả xưởng (FR-004).
                      */}
                    {row.type === PARENT_ROW && eff.missingHourDays > 0 && (
                        <Tooltip title="Những ngày này có sản lượng nhưng chưa nhập giờ công, nên không vào hiệu suất. Nhập ở màn Giờ làm thực tế.">
                            <span className="VcWarning VcHasTooltip VcMissingTag">
                                thiếu giờ {formatInt(eff.missingHourDays)} ngày
                            </span>
                        </Tooltip>
                    )}
                </span>
            )
        },
    },
    {
        title: header('efficiencyTrend', 'Xu hướng'),
        className: 'VcNewCol',
        key: 'efficiencyTrend',
        align: 'center',
        width: 100,
        render: (text, row) => {
            if (row.type !== 'actor') return <span className="VcZero">{DASH}</span>

            const eff = row.group && row.group.efficiency

            if (!eff || eff.notApplicable) return <span className="VcZero">{DASH}</span>

            return <Sparkline trend={eff.trend}/>
        },
    },
]

/**
 * Sáu cột của màn THEO CÔNG ĐOẠN: Giờ làm phân bổ · HS trung vị · Phân bố · Dưới ngưỡng ·
 * Chỉ số tương đối · Tín hiệu.
 *
 * Dòng CHA cần phân bố chứ không cần một con số duy nhất: "công đoạn này trung vị bao nhiêu, trải
 * từ đâu tới đâu, bao nhiêu người dưới ngưỡng" mới trả lời được câu hỏi của màn. Dòng CON (tài
 * khoản) thì dùng lại hai cột hiệu suất + chỉ số tương đối của màn kia.
 *
 * ⚠️ Vai của `PARENT_ROW`/`CHILD_ROW` phát biểu ở `helpers/reportRows.js` — MỘT chỗ, đừng chép
 *    bảng tra xuống đây. Đọc ngược vai là ba cột dưới đây trống ở mọi dòng, và không lỗi nào báo.
 */

/**
 * Hộp phân bố của một công đoạn: râu P10–P90, hộp P25–P75, vạch trung vị, vạch 100% (FR-005).
 *
 * ⚠️ Service đã trả `p25`/`p75` từ đầu nhưng không ai vẽ — cột này trước đây chỉ in hai con số
 *    "P10 – P90". Hai con số nói ĐỘ RỘNG mà không nói hình dạng: một công đoạn mà nửa số người
 *    dồn sát P10 trông y hệt một công đoạn chia đều, trong khi hai tình huống đó đòi hai cách xử
 *    lý khác nhau (đào tạo một nhóm, hay xem lại định mức).
 *
 * ⚠️ Vạch 100% là mốc ĐỊNH MỨC, vẽ riêng và khác màu: nó không thuộc phân bố, nó là cái phân bố
 *    đang được so với. Thang vẽ cắt ở [P10, P90] nên vạch này chỉ hiện khi 100% nằm trong khoảng
 *    đó — nằm ngoài thì cả công đoạn lệch hẳn một phía, và đó đã là việc của cột Tín hiệu.
 */
const SpreadBox = ({dist}) => {
    const geo = spreadGeometryOf(dist)
    const title = [
        `${dist.sampleSize} tài khoản đủ giờ phân bổ tối thiểu tại công đoạn này`,
        `P10 ${formatEfficiency(dist.p10)} · P25 ${formatEfficiency(dist.p25)} · trung vị ${formatEfficiency(dist.median)}`
            + ` · P75 ${formatEfficiency(dist.p75)} · P90 ${formatEfficiency(dist.p90)}`,
    ].join(' — ')

    return (
        <Tooltip title={title}>
            <span className="VcHasTooltip VcSpread">
                {/*
                  * `geo === null` khi mọi người bằng nhau tuyệt đối — KHÔNG vẽ hộp, chỉ in hai con
                  * số. Vẽ một hộp dồn hết về giữa là để người đọc đoán xem đó là chủ đích hay lỗi.
                  */}
                {geo && (
                    <span className="VcSpreadPlot">
                        <span className="VcSpreadWhisker"/>
                        {geo.box && (
                            <span className="VcSpreadBox" style={{left: `${geo.box.left}%`, width: `${geo.box.width}%`}}/>
                        )}
                        {geo.median !== null && <span className="VcSpreadMedian" style={{left: `${geo.median}%`}}/>}
                        {geo.target !== null && <span className="VcSpreadTarget" style={{left: `${geo.target}%`}}/>}
                    </span>
                )}
                <span className="VcSpreadText">
                    {formatEfficiency(dist.p10)} – {formatEfficiency(dist.p90)}
                </span>
            </span>
        </Tooltip>
    )
}

/** Nhãn cột Tín hiệu (BR-6.3) — chữ trước, màu sau; chữ là thứ đọc được khi in đen trắng. */
const SIGNAL_LABELS = {
    rate_off: {text: 'Định mức lệch?', className: 'VcDanger'},
    spread: {text: 'Chênh lệch lớn', className: 'VcWarning'},
    ok: {text: 'Bình thường', className: 'VcMuted'},
    unknown: {text: 'Chưa đủ mẫu', className: 'VcZero'},
}

export const stageEfficiencyColumns = ({header, thresholds, signalOf}) => [
    {
        title: header('allocatedHours', 'Giờ phân bổ'),
        className: 'VcNewCol',
        key: 'allocatedHours',
        align: 'right',
        width: 75,
        render: (text, row) => {
            const eff = efficiencyOfRow(row)

            if (!eff) return <span className="VcZero">{DASH}</span>
            if (eff.notApplicable) return <NotApplicable/>

            const hours = eff.allocatedHours

            return hours === null || hours === undefined ? (
                <span className="VcZero">{DASH}</span>
            ) : (
                <span>{formatHours(hours, 1)} h</span>
            )
        },
    },
    {
        /*
         * ⚠️ Nhãn là "HS trung VỊ", không phải "trung BÌNH". Ô này in `distribution.median` — bản
         *    trước gọi tên nó là trung bình, tức nhãn và con số nói hai thứ khác nhau trên cùng
         *    một ô, và người đi đối chiếu bằng Excel sẽ không bao giờ ra được con số trùng khớp.
         *    Trung vị là lựa chọn ĐÚNG ở đây (xem ghi chú trong `render`); cái sai là cái nhãn.
         */
        title: header('efficiency', 'HS trung vị'),
        className: 'VcNewCol',
        key: 'stageEfficiency',
        align: 'right',
        width: 65,
        render: (text, row) => {
            const eff = efficiencyOfRow(row)

            if (!eff) return <span className="VcZero">{DASH}</span>
            if (eff.notApplicable) return <NotApplicable/>

            /*
             * Dòng cha dùng TRUNG VỊ, không dùng trung bình: một người bỏ dở nửa buổi kéo trung bình
             * của cả công đoạn đi, còn trung vị thì không.
             *
             * ⚠️ Vì vậy dòng CHA ở màn này KHÔNG có dòng phụ "thuần": trung vị của hiệu suất thuần
             *    không bằng hiệu suất thuần của trung vị, và dán một tỷ số gộp ngay dưới một trung
             *    vị là mời người đọc trừ hai thống kê khác loại cho nhau. Dòng CON (tài khoản tại
             *    công đoạn) vẫn có đủ cặp số.
             */
            if (row.type === PARENT_ROW) {
                const median = eff.distribution ? eff.distribution.median : null

                return <EfficiencyValue value={median} tone={efficiencyToneOf(median, thresholds)}/>
            }

            return <EfficiencyCell eff={eff} thresholds={thresholds}/>
        },
    },
    {
        title: header('efficiencySpread', 'P10–P90'),
        className: 'VcNewCol',
        key: 'spread',
        align: 'center',
        width: 120,
        render: (text, row) => {
            if (row.type !== PARENT_ROW) return <span className="VcZero">{DASH}</span>

            const eff = efficiencyOfRow(row)
            const dist = eff && eff.distribution

            if (!dist || dist.p10 === null || dist.p90 === null) return <span className="VcZero">{DASH}</span>

            return <SpreadBox dist={dist}/>
        },
    },
    {
        title: header('belowThreshold', 'Dưới ngưỡng'),
        className: 'VcNewCol',
        key: 'below',
        align: 'right',
        width: 75,
        render: (text, row) => {
            if (row.type !== PARENT_ROW) return <span className="VcZero">{DASH}</span>

            const eff = efficiencyOfRow(row)
            const dist = eff && eff.distribution

            // Gác theo tập ĐANG LỌC, cùng tập với hai con số sắp in ra ngay dưới.
            const ratio = belowRatioOf(dist)

            if (!ratio) return <span className="VcZero">{DASH}</span>

            return (
                <Tooltip title={`Số tài khoản có hiệu suất dưới ${formatPercent(thresholds ? thresholds.b : 0.75, 0)}`}>
                    {/*
                      * ⚠️ Mẫu số là `scopedSampleSize` (NHÓM ĐANG LỌC), không phải `sampleSize`
                      *    (toàn xưởng). `below` đếm trong nhóm lọc theo BR-5.3, còn các phân vị đi
                      *    tập mốc theo BR-7.2 — ghép tử số của bên này với mẫu số của bên kia ra
                      *    `3/47` trên một bảng 5 dòng, một con số không mang nghĩa nào.
                      */}
                    <span className={ratio.below > 0 ? 'VcWarning VcHasTooltip' : 'VcHasTooltip'}>
                        {ratio.below}/{ratio.total}
                    </span>
                </Tooltip>
            )
        },
    },
    {
        /*
         * Dòng CON của màn theo công đoạn = một tài khoản TẠI công đoạn đó, nên chỉ số tương đối ở
         * đây chính là câu trả lời cho "người này so với mặt bằng của công đoạn này thì sao" — câu
         * hỏi của cả màn. Dòng CHA không có số này: trung vị so với chính nó luôn bằng 1,00.
         */
        title: header('relativeIndex', 'Chỉ số tương đối'),
        className: 'VcNewCol',
        key: 'stageRelativeIndex',
        align: 'right',
        width: 75,
        render: (text, row) => {
            if (row.type === PARENT_ROW) return <span className="VcZero">{DASH}</span>

            const eff = efficiencyOfRow(row)

            if (!eff) return <span className="VcZero">{DASH}</span>
            if (eff.notApplicable) return <NotApplicable/>
            if (eff.relativeIndex === null || eff.relativeIndex === undefined) {
                return <span className="VcZero">{DASH}</span>
            }

            const tone = relativeToneOf(eff.relativeIndex)

            return (
                <strong className={tone ? `VcEffValue VcEffValue--${tone}` : 'VcEffValue'}>
                    {formatDecimal(eff.relativeIndex, 2)}
                </strong>
            )
        },
    },
    {
        title: header('stageSignal', 'Tín hiệu'),
        className: 'VcNewCol',
        key: 'stageSignal',
        align: 'center',
        width: 130,
        render: (text, row) => {
            if (row.type !== PARENT_ROW) return <span className="VcZero">{DASH}</span>

            /*
             * Tín hiệu do SERVICE tính (`stageSignals`), không tính lại ở đây: ngưỡng 1,45 nằm
             * trong `pp_output_control_config` và PO chỉnh được — một bản dịch thứ hai ở FE là
             * một ngưỡng thứ hai, và nó sẽ trôi khỏi ngưỡng thật đúng vào lần PO chỉnh đầu tiên.
             */
            const signal = signalOf ? signalOf(row.group ? row.group.key : null) : null
            const label = SIGNAL_LABELS[signal]

            if (!label) return <span className="VcZero">{DASH}</span>

            return <span className={label.className}>{label.text}</span>
        },
    },
]


